import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
    ActivityIndicator,
    Modal,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import {
    ApiError,
    type SuggestedDay,
    type VisitSuggestion,
    type VisitSuggestionsResponse,
    askVisitSuggestions,
    fetchVisitSuggestions,
    requestVisit
} from "@/lib/api";
import { useNotification } from "@/lib/notifications";
import {
    colors,
    fonts,
    gradients,
    radius,
    shadows,
    spacing,
    typography,
    visitTypeColors
} from "@/lib/theme";
import { visitLocationLine } from "@/lib/visit-location";
import { useWebSocketEvent } from "@/lib/websocket";

// ═══════════════════════════════════════════════════
// Best-for-you Suggestions
//
// The nurse's mental model is her calendar with gaps, not a list of open
// visits — so the screen renders HER day (existing commitments as muted
// rows) with each suggestion slotted in at its bookable time, and says why
// it's good ("Right after your 10:00 AM visit · 1.2 mi away"). A visit is
// not one row — it's a flexible thing that can land in several places, so
// each card carries its best placement plus the other days/times it also
// fits. Narrowing to a single day upgrades the list to a composed PLAN.
//
// There are no filter widgets: the nurse just WRITES what she wants ("2 more
// visits on days I already work, close to my other patients"). An LLM parses
// her words into structured intent; the deterministic engine still owns
// matching and conflict-checking, so nothing unbookable can ever be shown.
// ═══════════════════════════════════════════════════

/** Day filter chips: the whole week plus each of the next 7 days. */
interface DayChip {
    key: string; // "" = whole week, else "YYYY-MM-DD"
    label: string;
}

function buildDayChips(): DayChip[] {
    const chips: DayChip[] = [{ key: "", label: "This week" }];
    const short = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate()
        ).padStart(2, "0")}`;
        const label =
            i === 0 ? "Today" : i === 1 ? "Tomorrow" : `${short[d.getDay()]} ${d.getDate()}`;
        chips.push({ key: iso, label });
    }
    return chips;
}

/** "9:00 AM" → minutes from midnight, for merging her visits with plan picks. */
function labelToMinutes(label: string): number {
    const m = label.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) {
        return 0;
    }
    let hour = Number(m[1]) % 12;
    if (m[3].toUpperCase() === "PM") {
        hour += 12;
    }
    return hour * 60 + Number(m[2]);
}

/** Tappable examples that fill the ask box — teaching by showing, instead of
 *  filter widgets. */
const EXAMPLE_ASKS = [
    "Fill my free days",
    "2 more visits on days I already work",
    "Close to my other patients",
    "Morning visits near home",
    "Build my best Saturday",
    "High-paying visits this weekend"
];

/** Where a suggestion stands in the one-tap request flow. */
type RequestState = "idle" | "working" | "done";

// ── Rows and cards ──

/** One of the nurse's own commitments, rendered as a muted anchor row so she
 *  can see exactly where a suggestion falls in her day. */
function ScheduleRow({ item }: { item: SuggestedDay["schedule"][number] }) {
    return (
        <View style={styles.scheduleRow}>
            <View style={styles.scheduleRail} />
            <MaterialCommunityIcons
                name={item.kind === "confirmed" ? "calendar-check" : "clock-outline"}
                size={14}
                color={colors.gray400}
            />
            <Text style={styles.scheduleText} numberOfLines={1}>
                {item.startLabel} – {item.endLabel} · {item.patientName}
            </Text>
            <Text style={styles.scheduleTag}>
                {item.kind === "confirmed" ? "Your visit" : "Pending"}
            </Text>
        </View>
    );
}

function SuggestionCard({
    suggestion,
    onPress,
    onPickDay,
    requestState,
    onQuickRequest
}: {
    suggestion: VisitSuggestion;
    onPress: () => void;
    onPickDay: (isoDate: string) => void;
    requestState: RequestState;
    onQuickRequest: () => void;
}) {
    const typeColors = visitTypeColors[suggestion.visitType] ?? visitTypeColors.SOC;
    return (
        <TouchableOpacity activeOpacity={0.85} onPress={onPress} style={styles.card}>
            <View style={styles.cardTopRow}>
                <View style={styles.cardTimeBlock}>
                    <Text style={styles.cardTime}>{suggestion.timeLabel}</Text>
                    <Text style={styles.cardDuration}>{suggestion.durationMinutes} min</Text>
                </View>
                <View style={styles.cardMiddle}>
                    <View style={styles.cardTypeRow}>
                        <View style={[styles.typePill, { backgroundColor: typeColors.bg }]}>
                            <Text style={[styles.typePillText, { color: typeColors.text }]}>
                                {suggestion.visitTypeDisplayName ?? suggestion.visitType}
                            </Text>
                        </View>
                        {suggestion.isNew && (
                            <View style={styles.newPill}>
                                <Text style={styles.newPillText}>NEW</Text>
                            </View>
                        )}
                    </View>
                    <Text style={styles.cardAddress} numberOfLines={1}>
                        Patient {suggestion.patientInitials} · {visitLocationLine(suggestion)}
                    </Text>
                </View>
                <View style={styles.cardPayBlock}>
                    <Text style={styles.cardPay}>${suggestion.payment}</Text>
                    <MaterialCommunityIcons
                        name="chevron-right"
                        size={18}
                        color={colors.textTertiary}
                    />
                </View>
            </View>
            {suggestion.reasons.length > 0 && (
                <View style={styles.reasonRow}>
                    {suggestion.reasons.map((reason) => (
                        <View key={reason} style={styles.reasonChip}>
                            <MaterialCommunityIcons
                                name="lightning-bolt"
                                size={11}
                                color={colors.teal600}
                            />
                            <Text style={styles.reasonText}>{reason}</Text>
                        </View>
                    ))}
                </View>
            )}
            {(suggestion.altTimes.length > 0 || suggestion.otherOptions.length > 0) && (
                <View style={styles.optionsRow}>
                    {suggestion.altTimes.length > 0 && (
                        <Text style={styles.altTimesText} numberOfLines={1}>
                            or {suggestion.altTimes.join(" · ")}
                        </Text>
                    )}
                    {suggestion.otherOptions.map((option) => (
                        <TouchableOpacity
                            key={option.isoDate}
                            activeOpacity={0.8}
                            style={styles.otherDayChip}
                            onPress={() => onPickDay(option.isoDate)}
                        >
                            <MaterialCommunityIcons
                                name="calendar-arrow-right"
                                size={11}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.otherDayChipText}>
                                {option.dayName.slice(0, 3)} {option.timeLabel}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>
            )}
            {/* One-tap request at the suggested slot — the whole point: no
                detail screen, no time picking. Tapping the card body still
                opens the full flow for a different time. */}
            <TouchableOpacity
                activeOpacity={0.85}
                disabled={requestState !== "idle"}
                onPress={onQuickRequest}
                testID={`quick-request-${suggestion.id}`}
            >
                {requestState === "done" ? (
                    <View style={styles.requestedRow}>
                        <MaterialCommunityIcons
                            name="check-circle"
                            size={15}
                            color={colors.success600}
                        />
                        <Text style={styles.requestedRowText}>
                            Requested — waiting for confirmation
                        </Text>
                    </View>
                ) : (
                    <LinearGradient
                        colors={[...gradients.teal]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.requestButton}
                    >
                        {requestState === "working" ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <MaterialCommunityIcons
                                name="calendar-plus"
                                size={15}
                                color="#FFFFFF"
                            />
                        )}
                        <Text style={styles.requestButtonText}>
                            Request for {suggestion.dayName.slice(0, 3)} {suggestion.timeLabel}
                        </Text>
                    </LinearGradient>
                )}
            </TouchableOpacity>
        </TouchableOpacity>
    );
}

function DayHeader({ day }: { day: SuggestedDay }) {
    return (
        <View style={styles.dayHeader}>
            <Text style={styles.dayName}>{day.dayName}</Text>
            <Text style={styles.dayDate}>{day.dateLabel}</Text>
            <Text style={styles.dayMeta}>
                {day.schedule.length > 0 ? `${day.schedule.length} on your schedule` : "Free day"}
            </Text>
        </View>
    );
}

// ── Screen ──

/** One row of the request-all confirmation popup. */
interface BatchItem {
    suggestion: VisitSuggestion;
    status: "pending" | "working" | "done" | "failed";
    error?: string;
}

export default function SuggestionsScreen() {
    const router = useRouter();
    const { showNotification } = useNotification();
    const [data, setData] = useState<VisitSuggestionsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [dayFilter, setDayFilter] = useState("");
    const [query, setQuery] = useState("");
    const [interpretation, setInterpretation] = useState<string | null>(null);
    const [askActive, setAskActive] = useState(false);
    // One-tap request state: which visits she already requested from here,
    // and which one is in flight right now.
    const [requestedIds, setRequestedIds] = useState<Set<number>>(new Set());
    const [requestingId, setRequestingId] = useState<number | null>(null);
    // Request-all popup: null = closed, else the rows with per-row status.
    const [batchItems, setBatchItems] = useState<BatchItem[] | null>(null);
    const [batchPhase, setBatchPhase] = useState<"confirm" | "running" | "done">("confirm");

    const chips = useMemo(buildDayChips, []);

    const loadDefault = useCallback((date: string) => {
        setLoading(true);
        setInterpretation(null);
        setAskActive(false);
        fetchVisitSuggestions(date ? { date } : undefined)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const submitAsk = useCallback((text: string) => {
        const q = text.trim();
        if (q.length < 3) {
            return;
        }
        setQuery(q);
        setDayFilter("");
        setLoading(true);
        setAskActive(true);
        askVisitSuggestions(q)
            .then((res) => {
                setData(res);
                setInterpretation(
                    res.understood ? res.interpretation : "Showing everything that fits for now."
                );
            })
            .catch(() => {
                setData(null);
                setInterpretation(null);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadDefault("");
    }, [loadDefault]);

    // Keep the list honest while she reads it — a visit someone else just took
    // disappears rather than dead-ending her on the request screen. Ask
    // results stay as-is (re-running the LLM on every WS event would be
    // wasteful), and mid-request/batch the list must not shift under her.
    useWebSocketEvent("visit:updated", (event) => {
        const { action } = event;
        if (askActive || batchItems != null || requestingId != null) {
            return;
        }
        if (action === "broadcasted" || action === "unbroadcasted" || action === "assigned") {
            loadDefault(dayFilter);
        }
    });

    const totals = data?.totals;
    const days = data?.days ?? [];
    // The server decides when a composed day plan applies (single-day scope,
    // whether from the day chips or from an ask like "tomorrow").
    const planMode = totals?.planCount != null && days.length <= 1;
    const clearAsk = useCallback(() => {
        setQuery("");
        loadDefault("");
    }, [loadDefault]);

    const openVisit = useCallback(
        (s: VisitSuggestion) => router.push(`/available-visit/${s.id}`),
        [router]
    );

    const pickDay = useCallback(
        (iso: string) => {
            setDayFilter(iso);
            loadDefault(iso);
        },
        [loadDefault]
    );

    // ── One-tap request + request-all ──

    const requestStateFor = useCallback(
        (id: number): RequestState =>
            requestedIds.has(id) ? "done" : requestingId === id ? "working" : "idle",
        [requestedIds, requestingId]
    );

    const quickRequest = useCallback(
        async (s: VisitSuggestion) => {
            setRequestingId(s.id);
            try {
                await requestVisit(s.id, s.isoDate, s.time);
                setRequestedIds((prev) => new Set(prev).add(s.id));
                showNotification(
                    `Requested ${s.dayName} ${s.timeLabel} — waiting for confirmation`,
                    "approved"
                );
            } catch (e) {
                showNotification(
                    e instanceof ApiError ? e.message : "Couldn't request this visit",
                    "failed"
                );
            } finally {
                setRequestingId(null);
            }
        },
        [showNotification]
    );

    const allSuggestions = useMemo(() => days.flatMap((d) => d.suggestions), [days]);
    /** What "Request all" covers: the composed plan in plan mode, otherwise
     *  everything on screen — minus anything she already requested. */
    const requestable = useMemo(
        () =>
            (planMode ? allSuggestions.filter((s) => s.inPlan) : allSuggestions).filter(
                (s) => !requestedIds.has(s.id)
            ),
        [allSuggestions, planMode, requestedIds]
    );

    const openBatch = useCallback(() => {
        setBatchItems(requestable.map((s) => ({ suggestion: s, status: "pending" as const })));
        setBatchPhase("confirm");
    }, [requestable]);

    const runBatch = useCallback(async () => {
        if (!batchItems) {
            return;
        }
        setBatchPhase("running");
        const items = [...batchItems];
        // Strictly sequential — the server re-checks her schedule per request,
        // so each accepted request correctly blocks a would-be overlap next.
        for (let i = 0; i < items.length; i++) {
            items[i] = { ...items[i], status: "working" };
            setBatchItems([...items]);
            const s = items[i].suggestion;
            try {
                await requestVisit(s.id, s.isoDate, s.time);
                items[i] = { ...items[i], status: "done" };
                setRequestedIds((prev) => new Set(prev).add(s.id));
            } catch (e) {
                items[i] = {
                    ...items[i],
                    status: "failed",
                    error: e instanceof ApiError ? e.message : "Couldn't request this visit"
                };
            }
            setBatchItems([...items]);
        }
        setBatchPhase("done");
    }, [batchItems]);

    const closeBatch = useCallback(() => {
        const finished = batchPhase === "done";
        setBatchItems(null);
        if (finished) {
            // Refresh so requested visits reappear as pending on her schedule.
            if (askActive && query) {
                submitAsk(query);
            } else {
                loadDefault(dayFilter);
            }
        }
    }, [batchPhase, askActive, query, submitAsk, loadDefault, dayFilter]);

    /** Plan mode: her commitments and plan picks merged into one itinerary. */
    const planItinerary = useMemo(() => {
        if (!planMode || days.length === 0) {
            return {
                rows: [] as Array<{ minutes: number; node: ReactNode }>,
                extras: [] as VisitSuggestion[]
            };
        }
        const day = days[0];
        const rows: Array<{ minutes: number; node: ReactNode }> = [];
        day.schedule.forEach((item, i) => {
            rows.push({
                minutes: labelToMinutes(item.startLabel),
                node: <ScheduleRow key={`sched-${i}`} item={item} />
            });
        });
        const extras: VisitSuggestion[] = [];
        for (const s of day.suggestions) {
            if (s.inPlan) {
                rows.push({
                    minutes: labelToMinutes(s.timeLabel),
                    node: (
                        <SuggestionCard
                            key={s.id}
                            suggestion={s}
                            onPress={() => openVisit(s)}
                            onPickDay={pickDay}
                            requestState={requestStateFor(s.id)}
                            onQuickRequest={() => quickRequest(s)}
                        />
                    )
                });
            } else {
                extras.push(s);
            }
        }
        rows.sort((a, b) => a.minutes - b.minutes);
        return { rows, extras };
    }, [planMode, days, openVisit, pickDay, requestStateFor, quickRequest]);

    return (
        <SafeAreaView style={styles.container} edges={["top"]}>
            {/* ── Header ── */}
            <View style={styles.header}>
                <TouchableOpacity
                    onPress={() => router.back()}
                    style={styles.backButton}
                    activeOpacity={0.7}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={26}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
                <View style={{ flex: 1 }}>
                    <Text style={styles.headerTitle}>Smart Picks</Text>
                    <Text style={styles.headerSub}>
                        {loading
                            ? "Matching open visits to your week…"
                            : totals && totals.suggestions > 0
                              ? `${totals.suggestions} visits · $${totals.pay} — all fit your schedule`
                              : "Open visits that fit your schedule"}
                    </Text>
                </View>
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.headerIcon}
                >
                    <MaterialCommunityIcons name="auto-fix" size={18} color="#FFFFFF" />
                </LinearGradient>
            </View>

            {/* ── Free-text ask ── */}
            <View style={styles.askBox}>
                <MaterialCommunityIcons name="magic-staff" size={16} color={colors.teal600} />
                <TextInput
                    style={styles.askInput}
                    value={query}
                    onChangeText={setQuery}
                    onSubmitEditing={() => submitAsk(query)}
                    returnKeyType="search"
                    placeholder="Tell me what you're looking for…"
                    placeholderTextColor={colors.textTertiary}
                    testID="ask-input"
                />
                <TouchableOpacity
                    activeOpacity={0.8}
                    onPress={() => submitAsk(query)}
                    disabled={query.trim().length < 3}
                    testID="ask-submit"
                >
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={[styles.askSubmit, query.trim().length < 3 && { opacity: 0.35 }]}
                    >
                        <MaterialCommunityIcons name="arrow-right" size={16} color="#FFFFFF" />
                    </LinearGradient>
                </TouchableOpacity>
            </View>

            {/* Understood-intent banner, or example asks to teach the box */}
            {askActive && interpretation ? (
                <View style={styles.interpretationRow}>
                    <MaterialCommunityIcons name="check-circle" size={14} color={colors.teal600} />
                    <Text style={styles.interpretationText} numberOfLines={2}>
                        {interpretation}
                    </Text>
                    <TouchableOpacity onPress={clearAsk} hitSlop={8} testID="ask-clear">
                        <MaterialCommunityIcons
                            name="close-circle"
                            size={16}
                            color={colors.textTertiary}
                        />
                    </TouchableOpacity>
                </View>
            ) : (
                !askActive && (
                    <View style={styles.examplesWrap}>
                        <Text style={styles.examplesLabel}>TAP TO TRY</Text>
                        <View style={styles.examplesRow}>
                            {EXAMPLE_ASKS.map((example) => (
                                <TouchableOpacity
                                    key={example}
                                    activeOpacity={0.8}
                                    style={styles.exampleChip}
                                    onPress={() => submitAsk(example)}
                                >
                                    <MaterialCommunityIcons
                                        name="gesture-tap"
                                        size={13}
                                        color={colors.teal600}
                                    />
                                    <Text style={styles.exampleChipText} numberOfLines={1}>
                                        {example}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>
                )
            )}

            {/* ── Day chips (quick scope; an ask replaces them) ── */}
            {!askActive && (
                <View>
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.chipsRow}
                    >
                        {chips.map((chip) => {
                            const selected = chip.key === dayFilter;
                            return (
                                <TouchableOpacity
                                    key={chip.key}
                                    activeOpacity={0.8}
                                    onPress={() => {
                                        setDayFilter(chip.key);
                                        loadDefault(chip.key);
                                    }}
                                    style={[styles.chip, selected && styles.chipSelected]}
                                >
                                    <Text
                                        style={[
                                            styles.chipText,
                                            selected && styles.chipTextSelected
                                        ]}
                                    >
                                        {chip.label}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>
                </View>
            )}

            {/* ── Body ── */}
            {loading ? (
                <View style={styles.centerFill}>
                    <ActivityIndicator size="large" color={colors.teal400} />
                    <Text style={styles.loadingText}>
                        {askActive ? "Reading your request…" : "Checking your schedule…"}
                    </Text>
                </View>
            ) : days.length === 0 ? (
                <View style={styles.centerFill}>
                    <MaterialCommunityIcons
                        name="calendar-search"
                        size={44}
                        color={colors.gray300}
                    />
                    <Text style={styles.emptyTitle}>Nothing that fits — yet</Text>
                    <Text style={styles.emptyText}>
                        No open visits match {askActive ? "that request" : "your week"} right now.
                        {askActive ? " Try asking a little more broadly." : ""} New visits are
                        posted all day.
                    </Text>
                </View>
            ) : planMode ? (
                <ScrollView
                    contentContainerStyle={styles.list}
                    showsVerticalScrollIndicator={false}
                >
                    {/* ── Composed day plan ── */}
                    <DayHeader day={days[0]} />
                    {totals!.planCount! > 0 && (
                        <LinearGradient
                            colors={[...gradients.brandSoft]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.planBanner}
                        >
                            <MaterialCommunityIcons
                                name="map-marker-path"
                                size={16}
                                color={colors.teal600}
                            />
                            <Text style={styles.planBannerText}>
                                Your best {days[0].dayName}: {totals!.planCount} visit
                                {totals!.planCount === 1 ? "" : "s"} that work together · adds $
                                {totals!.planPay}
                            </Text>
                        </LinearGradient>
                    )}
                    {planItinerary.rows.map((row) => row.node)}
                    {planItinerary.extras.length > 0 && (
                        <>
                            <Text style={styles.extrasHeader}>More options this day</Text>
                            {planItinerary.extras.map((s) => (
                                <SuggestionCard
                                    key={s.id}
                                    suggestion={s}
                                    onPress={() => openVisit(s)}
                                    onPickDay={pickDay}
                                    requestState={requestStateFor(s.id)}
                                    onQuickRequest={() => quickRequest(s)}
                                />
                            ))}
                        </>
                    )}
                    <Text style={styles.footerNote}>
                        The plan is a set of visits that fit together around your day, with travel
                        time to spare. Tap any visit to pick the exact time and request it.
                    </Text>
                </ScrollView>
            ) : (
                <ScrollView
                    contentContainerStyle={styles.list}
                    showsVerticalScrollIndicator={false}
                >
                    {days.map((day) => (
                        <View key={day.isoDate} style={styles.daySection}>
                            <DayHeader day={day} />
                            {day.schedule.map((item, i) => (
                                <ScheduleRow key={`${item.startLabel}-${i}`} item={item} />
                            ))}
                            {day.suggestions.map((s) => (
                                <SuggestionCard
                                    key={s.id}
                                    suggestion={s}
                                    onPress={() => openVisit(s)}
                                    onPickDay={pickDay}
                                    requestState={requestStateFor(s.id)}
                                    onQuickRequest={() => quickRequest(s)}
                                />
                            ))}
                        </View>
                    ))}
                    <Text style={styles.footerNote}>
                        Every suggestion already fits around your visits with travel time to spare.
                        Pick a day above — or just write what you want.
                    </Text>
                </ScrollView>
            )}

            {/* ── Request-all bar ── */}
            {!loading && requestable.length >= 2 && batchItems == null && (
                <View style={styles.batchBar} pointerEvents="box-none">
                    <TouchableOpacity activeOpacity={0.9} onPress={openBatch} testID="request-all">
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.batchBarButton}
                        >
                            <MaterialCommunityIcons
                                name="calendar-multiple-check"
                                size={16}
                                color="#FFFFFF"
                            />
                            <Text style={styles.batchBarText}>
                                Request all {requestable.length} · $
                                {Math.round(requestable.reduce((sum, x) => sum + x.payment, 0))}
                            </Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </View>
            )}

            {/* ── Request-all confirmation popup ── */}
            <Modal
                visible={batchItems != null}
                transparent
                animationType="fade"
                onRequestClose={closeBatch}
            >
                <View style={styles.modalBackdrop}>
                    <View style={styles.modalCard}>
                        <Text style={styles.modalTitle}>
                            {batchPhase === "done"
                                ? "All done"
                                : batchPhase === "running"
                                  ? "Requesting…"
                                  : "Request these visits?"}
                        </Text>
                        {batchPhase === "confirm" && (
                            <Text style={styles.modalSub}>
                                One request per visit, at the suggested time. The patient confirms
                                each one — you'll see them under My Requests.
                            </Text>
                        )}
                        <ScrollView style={{ maxHeight: 300 }}>
                            {(batchItems ?? []).map((item) => (
                                <View key={item.suggestion.id} style={styles.modalRow}>
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.modalRowTitle}>
                                            {item.suggestion.dayName.slice(0, 3)}{" "}
                                            {item.suggestion.dateLabel} ·{" "}
                                            {item.suggestion.timeLabel}
                                        </Text>
                                        <Text style={styles.modalRowSub} numberOfLines={1}>
                                            {item.suggestion.visitTypeDisplayName ??
                                                item.suggestion.visitType}{" "}
                                            · Patient {item.suggestion.patientInitials}
                                        </Text>
                                        {item.error != null && (
                                            <Text style={styles.modalRowError} numberOfLines={2}>
                                                {item.error}
                                            </Text>
                                        )}
                                    </View>
                                    <Text style={styles.modalRowPay}>
                                        ${item.suggestion.payment}
                                    </Text>
                                    {item.status === "pending" && (
                                        <MaterialCommunityIcons
                                            name="circle-outline"
                                            size={18}
                                            color={colors.gray300}
                                        />
                                    )}
                                    {item.status === "working" && (
                                        <ActivityIndicator size="small" color={colors.teal400} />
                                    )}
                                    {item.status === "done" && (
                                        <MaterialCommunityIcons
                                            name="check-circle"
                                            size={18}
                                            color={colors.success500}
                                        />
                                    )}
                                    {item.status === "failed" && (
                                        <MaterialCommunityIcons
                                            name="alert-circle"
                                            size={18}
                                            color={colors.error500}
                                        />
                                    )}
                                </View>
                            ))}
                        </ScrollView>
                        <View style={styles.modalTotalRow}>
                            <Text style={styles.modalTotalText}>
                                {batchPhase === "done"
                                    ? `${(batchItems ?? []).filter((i) => i.status === "done").length} requested` +
                                      ((batchItems ?? []).some((i) => i.status === "failed")
                                          ? ` · ${(batchItems ?? []).filter((i) => i.status === "failed").length} didn't go through`
                                          : "")
                                    : `${batchItems?.length ?? 0} visits · $${Math.round(
                                          (batchItems ?? []).reduce(
                                              (sum, i) => sum + i.suggestion.payment,
                                              0
                                          )
                                      )} total`}
                            </Text>
                        </View>
                        {batchPhase === "confirm" ? (
                            <View style={styles.modalButtons}>
                                <TouchableOpacity
                                    style={styles.modalCancel}
                                    activeOpacity={0.8}
                                    onPress={closeBatch}
                                >
                                    <Text style={styles.modalCancelText}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    activeOpacity={0.9}
                                    onPress={runBatch}
                                    testID="confirm-request-all"
                                    style={{ flex: 1 }}
                                >
                                    <LinearGradient
                                        colors={[...gradients.brand]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={styles.modalConfirm}
                                    >
                                        <Text style={styles.modalConfirmText}>Request all</Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            </View>
                        ) : batchPhase === "done" ? (
                            <View style={styles.modalButtons}>
                                <TouchableOpacity
                                    activeOpacity={0.9}
                                    onPress={closeBatch}
                                    testID="close-batch"
                                    style={{ flex: 1 }}
                                >
                                    <LinearGradient
                                        colors={[...gradients.teal]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={styles.modalConfirm}
                                    >
                                        <Text style={styles.modalConfirmText}>Done</Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            </View>
                        ) : null}
                    </View>
                </View>
            </Modal>
        </SafeAreaView>
    );
}

// ═══════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },

    // ── Header ──
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingHorizontal: spacing.base,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md
    },
    backButton: {
        width: 36,
        height: 36,
        borderRadius: radius.full,
        backgroundColor: colors.surface,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.xs
    },
    headerTitle: {
        ...typography.h1,
        color: colors.textPrimary
    },
    headerSub: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 1
    },
    headerIcon: {
        width: 36,
        height: 36,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.teal
    },

    // ── Ask box ──
    askBox: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginHorizontal: spacing.base,
        marginBottom: spacing.sm,
        paddingLeft: spacing.md,
        paddingRight: 6,
        paddingVertical: 6,
        borderRadius: radius.full,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        ...shadows.sm
    },
    askInput: {
        flex: 1,
        ...typography.bodySmall,
        color: colors.textPrimary,
        paddingVertical: 6
    },
    askSubmit: {
        width: 30,
        height: 30,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center"
    },
    interpretationRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginHorizontal: spacing.base,
        marginBottom: spacing.md,
        paddingHorizontal: spacing.md,
        paddingVertical: 8,
        borderRadius: radius.md,
        backgroundColor: colors.teal50
    },
    interpretationText: {
        ...typography.caption,
        color: colors.teal700,
        fontFamily: fonts.semiBold,
        flex: 1
    },
    examplesWrap: {
        paddingHorizontal: spacing.base,
        paddingBottom: spacing.md,
        gap: 6
    },
    examplesLabel: {
        ...typography.label,
        color: colors.textTertiary
    },
    examplesRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.xs
    },
    exampleChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: spacing.md,
        paddingVertical: 7,
        borderRadius: radius.full,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal200,
        maxWidth: 340
    },
    exampleChipText: {
        ...typography.caption,
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },

    // ── One-tap request ──
    requestButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: spacing.sm,
        paddingVertical: 9,
        borderRadius: radius.full
    },
    requestButtonText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    requestedRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: spacing.sm,
        paddingVertical: 9,
        borderRadius: radius.full,
        backgroundColor: colors.success50
    },
    requestedRowText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: colors.success600
    },

    // ── Request-all bar + popup ──
    batchBar: {
        position: "absolute",
        left: spacing.base,
        right: spacing.base,
        bottom: spacing.lg,
        alignItems: "center"
    },
    batchBarButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingHorizontal: spacing.xl,
        paddingVertical: 12,
        borderRadius: radius.full,
        ...shadows.brand
    },
    batchBarText: {
        ...typography.subtitle,
        color: "#FFFFFF"
    },
    modalBackdrop: {
        flex: 1,
        backgroundColor: "rgba(15, 10, 40, 0.45)",
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.lg
    },
    modalCard: {
        width: "100%",
        maxWidth: 420,
        borderRadius: radius.xl,
        backgroundColor: colors.surface,
        padding: spacing.lg,
        gap: spacing.sm,
        ...shadows.xl
    },
    modalTitle: {
        ...typography.h2,
        color: colors.textPrimary
    },
    modalSub: {
        ...typography.caption,
        color: colors.textSecondary
    },
    modalRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    modalRowTitle: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    modalRowSub: {
        ...typography.caption,
        color: colors.textSecondary
    },
    modalRowError: {
        ...typography.caption,
        color: colors.error600,
        marginTop: 2
    },
    modalRowPay: {
        ...typography.subtitle,
        color: colors.teal600
    },
    modalTotalRow: {
        paddingTop: spacing.xs
    },
    modalTotalText: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    modalButtons: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginTop: spacing.xs
    },
    modalCancel: {
        paddingHorizontal: spacing.lg,
        paddingVertical: 11,
        borderRadius: radius.full,
        backgroundColor: colors.gray100
    },
    modalCancelText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: colors.textSecondary
    },
    modalConfirm: {
        alignItems: "center",
        paddingVertical: 11,
        borderRadius: radius.full
    },
    modalConfirmText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },

    // ── Day chips ──
    chipsRow: {
        gap: spacing.sm,
        paddingHorizontal: spacing.base,
        paddingBottom: spacing.md
    },
    chip: {
        paddingHorizontal: spacing.md,
        paddingVertical: 7,
        borderRadius: radius.full,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border
    },
    chipSelected: {
        backgroundColor: colors.ink,
        borderColor: colors.ink
    },
    chipText: {
        ...typography.bodySmall,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary
    },
    chipTextSelected: {
        color: "#FFFFFF"
    },

    // ── Day sections ──
    list: {
        paddingHorizontal: spacing.base,
        // Extra clearance so the floating request-all bar never covers the
        // last card's request button.
        paddingBottom: spacing["5xl"] + spacing.xl
    },
    daySection: {
        marginBottom: spacing.lg
    },
    dayHeader: {
        flexDirection: "row",
        alignItems: "baseline",
        gap: spacing.sm,
        marginBottom: spacing.sm
    },
    dayName: {
        ...typography.h2,
        color: colors.textPrimary
    },
    dayDate: {
        ...typography.bodySmall,
        color: colors.textSecondary
    },
    dayMeta: {
        ...typography.caption,
        color: colors.textTertiary,
        marginLeft: "auto"
    },

    // ── Day-plan banner ──
    planBanner: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingHorizontal: spacing.md,
        paddingVertical: 10,
        borderRadius: radius.md,
        marginBottom: spacing.sm
    },
    planBannerText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: colors.teal700,
        flex: 1
    },
    extrasHeader: {
        ...typography.subtitle,
        color: colors.textSecondary,
        marginTop: spacing.lg,
        marginBottom: spacing.sm
    },

    // ── Her own schedule rows (anchors) ──
    scheduleRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: 6,
        paddingHorizontal: spacing.md,
        marginBottom: spacing.xs
    },
    scheduleRail: {
        width: 3,
        alignSelf: "stretch",
        borderRadius: radius.full,
        backgroundColor: colors.gray300
    },
    scheduleText: {
        ...typography.bodySmall,
        color: colors.textSecondary,
        flex: 1
    },
    scheduleTag: {
        ...typography.label,
        color: colors.textTertiary
    },

    // ── Suggestion cards ──
    card: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
        ...shadows.sm
    },
    cardTopRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md
    },
    cardTimeBlock: {
        alignItems: "center",
        minWidth: 64
    },
    cardTime: {
        ...typography.title,
        color: colors.textPrimary
    },
    cardDuration: {
        ...typography.caption,
        color: colors.textTertiary
    },
    cardMiddle: {
        flex: 1,
        gap: 3
    },
    cardTypeRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    typePill: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    typePillText: {
        ...typography.label
    },
    newPill: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: radius.full,
        backgroundColor: colors.pink50
    },
    newPillText: {
        ...typography.micro,
        color: colors.pink600
    },
    cardAddress: {
        ...typography.caption,
        color: colors.textSecondary
    },
    cardPayBlock: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2
    },
    cardPay: {
        ...typography.title,
        color: colors.teal600
    },
    reasonRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.xs,
        marginTop: spacing.sm
    },
    reasonChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
        backgroundColor: colors.teal50,
        borderRadius: radius.full,
        paddingHorizontal: spacing.sm,
        paddingVertical: 3
    },
    reasonText: {
        ...typography.caption,
        color: colors.teal600
    },

    // ── Flexible placements ("also fits") ──
    optionsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: spacing.xs,
        marginTop: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    altTimesText: {
        ...typography.caption,
        color: colors.textTertiary,
        marginRight: spacing.xs
    },
    otherDayChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
        backgroundColor: colors.gray100,
        borderRadius: radius.full,
        paddingHorizontal: spacing.sm,
        paddingVertical: 3
    },
    otherDayChipText: {
        ...typography.caption,
        color: colors.textSecondary
    },

    // ── Loading / empty states ──
    centerFill: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.md,
        paddingHorizontal: spacing["2xl"]
    },
    loadingText: {
        ...typography.body,
        color: colors.textSecondary
    },
    emptyTitle: {
        ...typography.h2,
        color: colors.textPrimary
    },
    emptyText: {
        ...typography.body,
        color: colors.textSecondary,
        textAlign: "center"
    },
    footerNote: {
        ...typography.caption,
        color: colors.textTertiary,
        textAlign: "center",
        marginTop: spacing.sm,
        paddingHorizontal: spacing.lg
    }
});
